import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SearchBox from "@/components/base/SearchBox";
import MovieCard from "@/components/feature/MovieCard";
import { mediaList } from "@/mocks/media";
import { searchMedia } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const results = useMemo(() => searchMedia(keyword), [keyword]);

  const hot = useMemo(
    () => [...mediaList].sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    []
  );

  useSeo({
    title: keyword
      ? `“${keyword}”的搜索结果 - 我的家庭教师`
      : "影视搜索 - 我的家庭教师",
    description: keyword
      ? `在我的家庭教师影视频道中搜索“${keyword}”，共找到 ${results.length} 部相关影视内容。`
      : "在我的家庭教师影视频道中搜索电影、电视剧、综艺等影视资源。",
    keywords: "我的家庭教师搜索,影视搜索,电影搜索,电视剧搜索",
    path: "/search",
  });

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-accent-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold tracking-wide text-foreground-950 md:text-3xl">
          影视搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          {keyword ? (
            <>
              当前搜索词：
              <span className="font-medium text-accent-400">{keyword}</span>
            </>
          ) : (
            "输入关键词，搜索想看的电影、电视剧或综艺"
          )}
        </p>
        <div className="mt-4 max-w-2xl">
          <SearchBox variant="page" defaultValue={keyword} />
        </div>
        <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-secondary-500/40 bg-secondary-500/10 px-3 py-1.5 text-xs text-secondary-200">
          <i className="ri-information-line text-sm"></i>
          演示模式：当前为前端模糊匹配演示，真实搜索接口待接入。
        </div>
      </header>

      {keyword && results.length > 0 && (
        <p className="mt-6 text-sm text-foreground-600">
          共找到 <span className="font-medium text-accent-400">{results.length}</span> 部相关影视
        </p>
      )}

      {keyword && results.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {results.map((item) => (
            <MovieCard key={item.id} item={item} showActors />
          ))}
        </div>
      ) : (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="mt-4 text-lg font-medium text-foreground-900">
              {keyword ? "没有找到相关影视内容" : "开始你的影视搜索"}
            </h3>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              {keyword
                ? "换个关键词试试，或从下方的热门推荐中挑选你喜欢的内容"
                : "在上方输入影片名称、主演、类型或地区，即可快速找到作品"}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-home-4-line text-base"></i>
                返回首页
              </Link>
              <Link
                to={`/category/${encodeURIComponent("电影")}`}
                className="flex h-10 items-center whitespace-nowrap rounded-md border border-background-300/60 px-5 text-sm text-foreground-800 transition-colors hover:text-foreground-950"
              >
                浏览电影分类
              </Link>
            </div>
          </div>

          <section className="mt-10">
            <h2 className="font-heading text-xl font-semibold tracking-wide text-foreground-950">
              热门推荐
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {hot.map((item) => (
                <MovieCard key={item.id} item={item} showActors />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}